import React, { createContext, useContext, useMemo } from 'react';

import propTypes from 'prop-types';

import {
  MessageTypes,
  Toast,
  useToast,
} from '@user-interviews/ui-design-system';

import { useTracking } from 'react-tracking';

import { connectedCalendarDetailsPropType } from 'components/connect_calendar/prop_types';
import { useAuthUserContext } from 'hooks/use_auth_user_context';
import { type SyncTrackingHook } from 'hooks/use_tracking_with_default_dispatch';

type ProjectWorkspaceProviderProps = {
  children: React.ReactNode;

  projectId: string;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  connectedCalendarDetails?: any;
  isAiFeaturesEnabled?: boolean;
  isRxEditDiscussionGuideEnabled?: boolean;
  isRxProjectSessionsChatEnabled?: boolean;
  isRxResearchAnalysisLaunchModalEnabled?: boolean;
  isRxSessionClipPageEnabled?: boolean;
  isRxSessionClipReelsEnabled?: boolean;
  isStaffExpandedProjectTypeTransitionsEnabled?: boolean;
  isWorkspaceEsTabCountsEnabled?: boolean;
  serverProject?: {
    isDraft: boolean;
    isPrivate: boolean | null;
    isUnmoderatedTask: boolean | null;
    isOneOnOne: boolean | null;
    isTwoPanelChatMode?: boolean | null;
    choicesToolEnabled?: boolean;
  };
};

type ProjectWorkspaceContextType = {
  projectId: string;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  connectedCalendarDetails?: any;
  isAiFeaturesEnabled: boolean;
  isRxEditDiscussionGuideEnabled: boolean;
  isRxProjectSessionsChatEnabled: boolean;
  isRxResearchAnalysisLaunchModalEnabled: boolean;
  isRxSessionClipPageEnabled: boolean;
  isRxSessionClipReelsEnabled: boolean;
  isStaffExpandedProjectTypeTransitionsEnabled: boolean;
  isWorkspaceEsTabCountsEnabled: boolean;
  serverProject?: {
    isDraft: boolean;
    isPrivate: boolean | null;
    isUnmoderatedTask: boolean | null;
    isOneOnOne: boolean | null;
    isTwoPanelChatMode?: boolean | null;
    choicesToolEnabled?: boolean;
  };
  setToastMessage: (options: {
    title?: string;
    type: (typeof MessageTypes)[keyof typeof MessageTypes];
    message: string;
  }) => void;
};

const ProjectWorkspaceContext = createContext<ProjectWorkspaceContextType>(
  {} as ProjectWorkspaceContextType,
);

// oxlint-disable-next-line local-rules/max-props
export function ProjectWorkspaceProvider({
  children,
  isAiFeaturesEnabled,
  isRxEditDiscussionGuideEnabled,
  isRxProjectSessionsChatEnabled,
  isRxResearchAnalysisLaunchModalEnabled,
  isRxSessionClipPageEnabled,
  isRxSessionClipReelsEnabled,
  isStaffExpandedProjectTypeTransitionsEnabled,
  isWorkspaceEsTabCountsEnabled,
  connectedCalendarDetails,
  projectId,
  serverProject,
}: ProjectWorkspaceProviderProps) {
  const authUserContext = useAuthUserContext();
  const { dismissMessage, messages, setMessage } = useToast();

  const { Track } = useTracking({
    eventData: {
      project_id: projectId,
      ...(authUserContext?.account && {
        account_id: authUserContext.account.id,
      }),
      ...(authUserContext?.user && { user_id: authUserContext.user.id }),
    },
  }) as SyncTrackingHook;

  const contextValue = useMemo(
    () => ({
      connectedCalendarDetails,
      projectId,
      serverProject,
      isAiFeaturesEnabled: !!isAiFeaturesEnabled,
      isRxEditDiscussionGuideEnabled: !!isRxEditDiscussionGuideEnabled,
      isRxProjectSessionsChatEnabled: !!isRxProjectSessionsChatEnabled,
      isRxResearchAnalysisLaunchModalEnabled:
        !!isRxResearchAnalysisLaunchModalEnabled,
      isRxSessionClipPageEnabled: !!isRxSessionClipPageEnabled,
      isRxSessionClipReelsEnabled: !!isRxSessionClipReelsEnabled,
      isStaffExpandedProjectTypeTransitionsEnabled:
        !!isStaffExpandedProjectTypeTransitionsEnabled,
      isWorkspaceEsTabCountsEnabled: !!isWorkspaceEsTabCountsEnabled,
      setToastMessage: setMessage,
    }),
    [
      connectedCalendarDetails,
      projectId,
      serverProject,
      isAiFeaturesEnabled,
      isRxEditDiscussionGuideEnabled,
      isRxProjectSessionsChatEnabled,
      isRxResearchAnalysisLaunchModalEnabled,
      isRxSessionClipPageEnabled,
      isRxSessionClipReelsEnabled,
      isStaffExpandedProjectTypeTransitionsEnabled,
      isWorkspaceEsTabCountsEnabled,
      setMessage,
    ],
  );

  return (
    <ProjectWorkspaceContext.Provider value={contextValue}>
      <Toast autoDismiss messages={messages} onToastClosed={dismissMessage} />
      <Track>{children}</Track>
    </ProjectWorkspaceContext.Provider>
  );
}

ProjectWorkspaceProvider.propTypes = {
  connectedCalendarDetails: connectedCalendarDetailsPropType,
  isAiFeaturesEnabled: propTypes.bool,
  projectId: propTypes.string.isRequired,
  serverProject: propTypes.object,
};

/**
 * Returns the surrounding `ProjectWorkspaceProvider` context (project ID,
 * feature flags, server-rendered project metadata, toast helper).
 */
export function useProjectWorkspaceContext() {
  return useContext(ProjectWorkspaceContext);
}
